/**
 * Chart Style Configurations
 * Defines color schemes and styling templates for blood test charts
 */

// Color palettes
export const COLOR_SCHEMES = {
  DEFAULT: {
    id: 'default',
    name: 'Default Blue',
    primary: '#3b82f6',
    secondary: '#60a5fa',
    success: '#22c55e',
    warning: '#f59e0b',
    danger: '#ef4444',
    grid: '#e5e7eb',
    text: '#374151',
    normalRangeMin: '#22c55e',
    normalRangeMax: '#22c55e',
    abnormalHigh: '#ef4444',
    abnormalLow: '#f59e0b'
  },
  MEDICAL: {
    id: 'medical',
    name: 'Medical Theme',
    primary: '#0891b2', // cyan
    secondary: '#06b6d4',
    success: '#10b981', // emerald
    warning: '#f59e0b', // amber
    danger: '#dc2626', // red
    grid: '#e5e7eb',
    text: '#1f2937',
    normalRangeMin: '#10b981',
    normalRangeMax: '#10b981',
    abnormalHigh: '#dc2626',
    abnormalLow: '#f59e0b'
  },
  DARK: {
    id: 'dark',
    name: 'Dark Theme',
    primary: '#60a5fa',
    secondary: '#93c5fd',
    success: '#34d399',
    warning: '#fbbf24',
    danger: '#f87171',
    grid: '#4b5563',
    text: '#f9fafb',
    normalRangeMin: '#34d399',
    normalRangeMax: '#34d399',
    abnormalHigh: '#f87171',
    abnormalLow: '#fbbf24'
  },
  COLORFUL: {
    id: 'colorful',
    name: 'Colorful',
    primary: '#8b5cf6', // violet
    secondary: '#a78bfa',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    grid: '#e5e7eb',
    text: '#374151',
    normalRangeMin: '#10b981',
    normalRangeMax: '#10b981',
    abnormalHigh: '#ef4444',
    abnormalLow: '#f59e0b'
  },
  PROFESSIONAL: {
    id: 'professional',
    name: 'Professional',
    primary: '#1e40af', // blue-800
    secondary: '#3b82f6', // blue-500
    success: '#047857', // emerald-700
    warning: '#d97706', // amber-600
    danger: '#b91c1c', // red-700
    grid: '#d1d5db',
    text: '#111827',
    normalRangeMin: '#047857',
    normalRangeMax: '#047857',
    abnormalHigh: '#b91c1c',
    abnormalLow: '#d97706'
  }
};

// Chart template configurations
export const CHART_TEMPLATES = {
  DEFAULT: {
    id: 'default',
    name: 'Default Line Chart',
    type: 'line',
    config: {
      lineType: 'monotone', // monotone, linear, step, stepBefore, stepAfter
      strokeWidth: 3,
      dotSize: 3,
      activeDotSize: 4,
      showDots: true,
      showGrid: true,
      showLegend: false,
      showTooltip: true,
      showNormalRange: true,
      animationDuration: 300,
      gridDasharray: '3 3',
      height: 400,
      fontSize: 12,
      margin: { top: 20, right: 30, left: 20, bottom: 5 }
    }
  },
  MINIMAL: {
    id: 'minimal',
    name: 'Minimal',
    type: 'line',
    config: {
      lineType: 'monotone',
      strokeWidth: 2,
      dotSize: 2,
      activeDotSize: 3,
      showDots: false,
      showGrid: false,
      showLegend: false,
      showTooltip: true,
      showNormalRange: true,
      animationDuration: 0,
      gridDasharray: '0 0',
      height: 300,
      fontSize: 11,
      margin: { top: 10, right: 20, left: 10, bottom: 5 }
    }
  },
  DETAILED: {
    id: 'detailed',
    name: 'Detailed View',
    type: 'line',
    config: {
      lineType: 'monotone',
      strokeWidth: 4,
      dotSize: 4,
      activeDotSize: 5,
      showDots: true,
      showGrid: true,
      showLegend: false,
      showTooltip: true,
      showNormalRange: true,
      animationDuration: 500,
      gridDasharray: '3 3',
      height: 500,
      fontSize: 14,
      margin: { top: 25, right: 40, left: 25, bottom: 10 }
    }
  },
  COMPACT: {
    id: 'compact',
    name: 'Compact',
    type: 'line',
    config: {
      lineType: 'linear',
      strokeWidth: 2,
      dotSize: 1.5,
      activeDotSize: 2.5,
      showDots: false,
      showGrid: true,
      showLegend: false,
      showTooltip: true,
      showNormalRange: false,
      animationDuration: 200,
      gridDasharray: '2 2',
      height: 250,
      fontSize: 10,
      margin: { top: 10, right: 15, left: 10, bottom: 5 }
    }
  },
  PRESENTATION: {
    id: 'presentation',
    name: 'Presentation',
    type: 'line',
    config: {
      lineType: 'monotone',
      strokeWidth: 5,
      dotSize: 5,
      activeDotSize: 6,
      showDots: true,
      showGrid: true,
      showLegend: false,
      showTooltip: true,
      showNormalRange: true,
      animationDuration: 800,
      gridDasharray: '5 5',
      height: 600,
      fontSize: 16,
      margin: { top: 30, right: 50, left: 30, bottom: 15 }
    }
  }
};

/**
 * Get color scheme by ID
 * @param {string} schemeId - Color scheme ID
 * @returns {object} - Color scheme object
 */
export function getColorScheme(schemeId = 'default') {
  return COLOR_SCHEMES[schemeId.toUpperCase()] || COLOR_SCHEMES.DEFAULT;
}

/**
 * Get chart template by ID
 * @param {string} templateId - Template ID
 * @returns {object} - Template configuration object
 */
export function getChartTemplate(templateId = 'default') {
  return CHART_TEMPLATES[templateId.toUpperCase()] || CHART_TEMPLATES.DEFAULT;
}

/**
 * Merge custom config with template
 * @param {string} templateId - Template ID
 * @param {object} customConfig - Custom configuration overrides
 * @returns {object} - Merged configuration
 */
export function mergeChartConfig(templateId, customConfig = {}) {
  const template = getChartTemplate(templateId);
  return {
    ...template,
    config: {
      ...template.config,
      ...customConfig
    }
  };
}

/**
 * Get color for test result based on normal range
 * @param {number} value - Test result value
 * @param {number} normalMin - Normal range minimum
 * @param {number} normalMax - Normal range maximum
 * @param {object} colorScheme - Color scheme to use
 * @returns {string} - Color hex code
 */
export function getValueColor(value, normalMin, normalMax, colorScheme = COLOR_SCHEMES.DEFAULT) {
  if (!value || (!normalMin && !normalMax)) {
    return colorScheme.primary;
  }

  if (normalMin && value < normalMin) {
    return colorScheme.abnormalLow;
  }

  if (normalMax && value > normalMax) {
    return colorScheme.abnormalHigh;
  }

  return colorScheme.success;
}

/**
 * Default chart settings
 */
export const DEFAULT_CHART_SETTINGS = {
  colorScheme: 'default',
  template: 'default',
  showNormalRange: true,
  showGrid: true,
  showLegend: true,
  showTooltip: true,
  dateFormat: 'MMM DD, YYYY',
  timeFormat: 'HH:mm',
  numberFormat: '0,0.00'
};
